import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Loader2, User, Building2, FileUp, Pencil, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import { PartnerQuote } from "./PartnerQuote";
import RoofMap from "./RoofMap";

const empty = {
  entity_type: "persona_fisica", nome: "", cognome: "", ragione_sociale: "", partita_iva: "",
  codice_fiscale: "", email: "", phone: "", address: "", citta: "",
  target: "residenziale", consumo_annuo_kwh: "",
};

export default function PartnerNewProposal({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState(empty);
  const [consMethod, setConsMethod] = useState("manuale"); // manuale | bolletta
  const [billFile, setBillFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selfToken, setSelfToken] = useState(null);
  const [roofSummary, setRoofSummary] = useState(null);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const isAzienda = f.entity_type === "azienda";

  const reset = () => { setStep(1); setF(empty); setConsMethod("manuale"); setBillFile(null); setSelfToken(null); setRoofSummary(null); setBusy(false); };
  const close = (v) => { onOpenChange(v); if (!v) setTimeout(reset, 200); };

  const valid = () => {
    if (isAzienda) { if (!f.ragione_sociale.trim() || !/^\d{11}$/.test(f.partita_iva.replace(/\s/g, ""))) return false; }
    else { if (!f.nome.trim() || !f.cognome.trim()) return false; }
    if (!f.address.trim() && !f.citta.trim()) return false;
    if (consMethod === "manuale" && !(parseFloat(f.consumo_annuo_kwh) > 0)) return false;
    if (consMethod === "bolletta" && !billFile) return false;
    return true;
  };

  const submit = async () => {
    if (!valid()) { toast.error("Compila i campi obbligatori (cliente, comune e consumo/bolletta)."); return; }
    setBusy(true);
    try {
      const payload = {
        entity_type: f.entity_type, nome: f.nome, cognome: f.cognome,
        ragione_sociale: f.ragione_sociale, partita_iva: f.partita_iva, codice_fiscale: f.codice_fiscale,
        email: f.email, phone: f.phone, address: f.address, citta: f.citta, target: f.target,
        consumo_annuo_kwh: consMethod === "manuale" ? parseFloat(f.consumo_annuo_kwh) : null,
      };
      const { data } = await api.post("/partner/leads/new", payload);
      const token = data.self_token;
      // Analisi consumi: bolletta (LLM) oppure consumo manuale già salvato alla creazione
      if (consMethod === "bolletta" && billFile) {
        const fd = new FormData();
        fd.append("file", billFile);
        toast.info("Analisi bolletta in corso, può richiedere fino a 2 minuti…");
        await api.post(`/public/self/${token}/bill`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 150000 });
      }
      // Produzione PVGIS dal comune (best-effort, non blocca)
      try {
        await api.post(`/public/self/${token}/pvgis`, { address: f.citta || f.address || null });
      } catch (e) { /* la produzione si può ricalcolare in seguito */ }
      setSelfToken(token);
      setStep(2);
      onCreated && onCreated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Creazione non riuscita");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="partner-new-proposal-dialog">
        <DialogHeader>
          <DialogTitle>{step === 1 ? "Nuova proposta — dati cliente" : step === 2 ? "Nuova proposta — tetto da satellite" : "Nuova proposta — configuratore"}</DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            {/* Tipo soggetto */}
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => set("entity_type", "persona_fisica")} data-testid="np-tipo-persona"
                className={`flex items-center justify-center gap-2 rounded border px-3 py-2 text-sm font-medium transition-colors ${!isAzienda ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                <User className="h-4 w-4" /> Persona fisica
              </button>
              <button type="button" onClick={() => set("entity_type", "azienda")} data-testid="np-tipo-azienda"
                className={`flex items-center justify-center gap-2 rounded border px-3 py-2 text-sm font-medium transition-colors ${isAzienda ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                <Building2 className="h-4 w-4" /> Azienda
              </button>
            </div>

            {isAzienda ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ragione sociale *" className="col-span-2"><Input value={f.ragione_sociale} onChange={(e) => set("ragione_sociale", e.target.value)} data-testid="np-ragione-sociale" /></Field>
                <Field label="Partita IVA *"><Input value={f.partita_iva} onChange={(e) => set("partita_iva", e.target.value)} placeholder="11 cifre" data-testid="np-piva" /></Field>
                <Field label="Referente"><Input value={f.nome} onChange={(e) => set("nome", e.target.value)} placeholder="Nome referente" data-testid="np-referente" /></Field>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nome *"><Input value={f.nome} onChange={(e) => set("nome", e.target.value)} data-testid="np-nome" /></Field>
                <Field label="Cognome *"><Input value={f.cognome} onChange={(e) => set("cognome", e.target.value)} data-testid="np-cognome" /></Field>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Per inviare la proposta" data-testid="np-email" /></Field>
              <Field label="Telefono"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="np-phone" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Indirizzo cantiere" className="col-span-2 sm:col-span-1"><Input value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Via e civico" data-testid="np-address" /></Field>
              <Field label="Comune *"><Input value={f.citta} onChange={(e) => set("citta", e.target.value)} placeholder="Es. Brescia" data-testid="np-citta" /></Field>
            </div>
            <p className="text-[11px] text-amber-600">Inserisci il solo <b>Comune</b> per il calcolo della produzione (PVGIS).</p>

            {/* Consumo */}
            <div className="rounded border border-border p-3 space-y-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Consumo del cliente</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setConsMethod("manuale")} data-testid="np-cons-manuale"
                  className={`flex items-center justify-center gap-2 rounded border px-3 py-2 text-sm font-medium transition-colors ${consMethod === "manuale" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                  <Pencil className="h-4 w-4" /> Consumo a mano
                </button>
                <button type="button" onClick={() => setConsMethod("bolletta")} data-testid="np-cons-bolletta"
                  className={`flex items-center justify-center gap-2 rounded border px-3 py-2 text-sm font-medium transition-colors ${consMethod === "bolletta" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
                  <FileUp className="h-4 w-4" /> Carica bolletta
                </button>
              </div>
              {consMethod === "manuale" ? (
                <Field label="Consumo annuo (kWh) *"><Input type="number" min="0" step="100" value={f.consumo_annuo_kwh} onChange={(e) => set("consumo_annuo_kwh", e.target.value)} placeholder="Es. 3500" data-testid="np-consumo" /></Field>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Bolletta (PDF o immagine) *</Label>
                  <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setBillFile(e.target.files?.[0] || null)} data-testid="np-bill-file" />
                  {billFile && <p className="text-[11px] text-muted-foreground">{billFile.name}</p>}
                  <p className="text-[11px] text-red-500">L'analisi della bolletta può richiedere fino a 120 secondi. Non chiudere la finestra.</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => close(false)} data-testid="np-cancel">Annulla</Button>
              <Button onClick={submit} disabled={busy || !valid()} data-testid="np-continue">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-1.5" />}
                Continua al tetto
              </Button>
            </div>
          </div>
        ) : step === 2 ? (
          <div className="space-y-3" data-testid="np-step-tetto">
            <p className="text-sm text-muted-foreground">Cliente <b className="text-foreground">{isAzienda ? f.ragione_sociale : `${f.nome} ${f.cognome}`}</b> creato. Individua il tetto da satellite e disegna le falde: è un passaggio <b>obbligatorio</b> prima della configurazione.</p>
            {selfToken && (
              <RoofMap
                lead={{ id: null, address: f.address || f.citta, indirizzo_residenza: f.address || f.citta, city: f.citta, roof: null }}
                selfToken={selfToken}
                onUpdate={(l) => setRoofSummary(l.roof?.summary || null)}
              />
            )}
            {roofSummary && (
              <div className="rounded border border-primary/40 bg-primary/5 p-3 flex items-center gap-2 text-primary text-sm font-semibold" data-testid="np-tetto-ok">
                <CheckCircle2 className="h-4 w-4 shrink-0" /> Misurazione salvata · netta {Math.round(roofSummary.netta_m2 || 0)} m²{roofSummary.potenza_kwp ? ` · ${roofSummary.potenza_kwp} kWp installabili` : ""}
              </div>
            )}
            <div className="flex items-center justify-between pt-1">
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="np-tetto-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Indietro</Button>
              <Button onClick={() => setStep(3)} disabled={!roofSummary} data-testid="np-tetto-next">Avanti alla configurazione <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            </div>
            {!roofSummary && <p className="text-[11px] text-amber-600 text-right">Disegna le falde sul tetto e premi “Salva misurazione” per proseguire.</p>}
          </div>
        ) : (
          <div>
            <p className="text-sm text-muted-foreground mb-2">Configura l'impianto e genera la proposta per <b className="text-foreground">{isAzienda ? f.ragione_sociale : `${f.nome} ${f.cognome}`}</b>.</p>
            {selfToken && <PartnerQuote selfToken={selfToken} preAuthed />}
            <div className="flex justify-end pt-3">
              <Button variant="outline" onClick={() => close(false)} data-testid="np-done">Chiudi</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
